/**
 * CaptionerNode (Canvas V2)
 *
 * Burns word-anchored captions into a finished video. The backend agent calls
 * the hypit sidecar (WhisperX word alignment + caption render); this node is
 * the canvas surface for it.
 *
 * Deliberately small. A captioner has one input, one meaningful output and two
 * settings, so it renders the captioned video and the caption text itself —
 * the two things you actually check after a run. Everything else (language,
 * style) is a config field the generic inspector already edits.
 *
 * Input:  in_video (video)
 * Output: out_video (video), out_transcript (timed caption cues)
 */

import { memo, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { NodeProps, Position } from 'reactflow'
import { getAssetUrl, type CaptionerNodeData, type CaptionWordDict } from '@ponpon/api-client'
import { BaseNode, VideoPreviewPlayer, useNodeActions } from '../shared'
import { TypedHandle } from '../../TypedHandle'
import { useProjectId } from '../../../../contexts/ProjectIdContext'

const CaptionerNode = memo(function CaptionerNode(props: NodeProps<CaptionerNodeData>) {
  const { data, selected, id } = props
  const [hovering, setHovering] = useState(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const projectId = useProjectId()
  const { handleDownload } = useNodeActions({ nodeId: id })

  const videoUrl =
    data.out_video && projectId ? getAssetUrl(projectId, data.out_video) : null
  // "cues", not "words": karaoke gives one entry per word, plain gives one per
  // spoken line in the same field, and plain is what runs until the sidecar is
  // deployed. Counting lines as words would misreport every run this ships.
  const cues = data.out_transcript ?? []
  const language = data.in_language ?? 'zh'
  const style = data.in_caption_style ?? 'karaoke'

  // The first cues are the cheapest possible check that the right clip was
  // captioned — a mismatch here is obvious at a glance, where a silent
  // mis-transcription would otherwise only surface on playback.
  const preview = cues
    .slice(0, 12)
    .map((w: CaptionWordDict) => w.word)
    .join(' ')

  // VideoPreviewPlayer renders each control only when the flag AND its callback
  // are both supplied, so passing showExpand/showDownload alone rendered
  // neither — the captioned cut could not be opened or saved from the canvas.
  const onDownload = useCallback(() => {
    if (!data.out_video) return
    handleDownload(data.out_video, `${data.in_name || 'captions'}.mp4`)
  }, [data.out_video, data.in_name, handleDownload])

  return (
    <div onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
      <TypedHandle
        type="target"
        position={Position.Left}
        id="in_video"
        portType="video"
        showTooltip
      />

      <BaseNode
        title={data.in_name || 'Captions'}
        icon="💬"
        selected={selected}
        status={data.status}
      >
        {videoUrl ? (
          <VideoPreviewPlayer
            videoUrl={videoUrl}
            isHovering={hovering}
            showExpand
            showDownload
            onExpand={() => setIsVideoModalOpen(true)}
            onDownload={onDownload}
          />
        ) : (
          <div className="flex h-24 items-center justify-center text-xs text-muted-foreground">
            Connect a rendered video, then Generate
          </div>
        )}

        <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>
            {language} · {style}
          </span>
          <span>{cues.length > 0 ? `${cues.length} cues` : 'no captions yet'}</span>
        </div>

        {preview && (
          <div className="mt-1 line-clamp-2 text-[10px] leading-snug text-muted-foreground/80">
            {preview}
          </div>
        )}
      </BaseNode>

      <TypedHandle
        type="source"
        position={Position.Right}
        id="out_video"
        portType="video"
        showTooltip
      />

      {isVideoModalOpen &&
        videoUrl &&
        createPortal(
          <div
            data-testid="video-lightbox-backdrop"
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-8 backdrop-blur-sm"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <div
              data-testid="video-lightbox"
              className="relative flex max-h-full w-full max-w-6xl items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="video-lightbox-close"
                onClick={() => setIsVideoModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary transition-colors hover:text-foreground"
              >
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <video
                src={videoUrl}
                controls
                autoPlay
                className="max-h-[85vh] max-w-full rounded-lg bg-black shadow-2xl"
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
})

export { CaptionerNode }
